import { useEffect } from "react";

// ─── usePageMeta ──────────────────────────────────────────────────────────────
// Sets document.title and <meta name="description">/<meta name="robots"> for
// the currently-mounted public page, restoring the previous values on unmount.
// react-snap runs the real app in a headless browser and captures the final
// DOM, so whatever this hook sets ends up baked into that page's prerendered
// static HTML — this is what makes per-page SEO metadata and the legal pages'
// `noindex` actually take effect, despite the app itself being a client SPA.
function upsertMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  const existed = !!tag;
  const previousContent = tag?.getAttribute("content") ?? null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return () => {
    if (!existed) {
      tag.remove();
    } else if (previousContent !== null) {
      tag.setAttribute("content", previousContent);
    }
  };
}

export function usePageMeta({ title, description, robots } = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const cleanups = [];
    if (description) cleanups.push(upsertMeta("description", description));
    if (robots) cleanups.push(upsertMeta("robots", robots));

    return () => {
      if (title) document.title = previousTitle;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [title, description, robots]);
}
